import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Shield,
    AlertTriangle,
    Mail,
    Smartphone,
    Wifi,
    Phone,
    Home,
    Lock,
    Trash2,
    Loader2,
    RefreshCw,
} from "lucide-react";
import { Button } from "../ui/button";
import {
    adminGetSecurityConfig,
    adminSetSecurityConfig,
    adminListAbuseEvents,
    adminClearAbuseEvents,
} from "../../lib/api";

/**
 * Admin → Security (iter-97).
 *
 *  - Toggle switches for the 6 abuse-prevention rules
 *  - Configurable soft-delete grace period (7-90 days)
 *  - Abuse report table with attempted email / reason / IP / device
 *
 * Strict-superadmin gating happens on the backend — reviewers can view
 * but every save call returns 403 for them. The UI hides the save
 * button when the user is a reviewer.
 */

const TOGGLES = [
    { key: "limit_per_email", label: "Email address", icon: Mail, locked: true,
      hint: "Always on — duplicate emails would break login." },
    { key: "limit_per_device", label: "Device ID", icon: Smartphone,
      hint: "Capacitor device UUID on native + browser fingerprint on web." },
    { key: "limit_per_ip", label: "IP address", icon: Wifi,
      hint: "Risky in offices/schools where many users share an IP. Off by default." },
    { key: "limit_per_phone", label: "Phone number", icon: Phone,
      hint: "Matches by digit-only normalisation." },
    { key: "limit_per_home_address", label: "Home address", icon: Home,
      hint: "Matches by lowercased alphanumeric normalisation." },
    { key: "limit_per_google", label: "Google account", icon: Lock, locked: true,
      hint: "Always on — Google `sub` IDs are globally unique." },
];

export default function SecurityTab({ isReviewer = false }) {
    const [cfg, setCfg] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        try {
            const [c, e] = await Promise.all([
                adminGetSecurityConfig(),
                adminListAbuseEvents(200),
            ]);
            setCfg(c);
            setEvents(e?.events || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load security config");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading || !cfg) {
        return (
            <div data-testid="security-tab-loading" className="text-sm text-slate-500">
                Loading security config…
            </div>
        );
    }

    const updateField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const onSave = async () => {
        setSaving(true);
        try {
            const next = await adminSetSecurityConfig(cfg);
            setCfg(next);
            toast.success("Security settings saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await load();
    };

    const onClearLog = async () => {
        if (!confirm("Clear all abuse events? This cannot be undone.")) return;
        try {
            await adminClearAbuseEvents();
            setEvents([]);
            toast.success("Abuse log cleared");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to clear log");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-security-tab">
            {/* Abuse report */}
            <section
                data-testid="abuse-events-card"
                className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 md:p-5"
            >
                <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                        Abuse report
                    </h3>
                    <span
                        data-testid="abuse-events-count"
                        className="text-xs text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-sm"
                    >
                        {events.length} event{events.length === 1 ? "" : "s"}
                    </span>
                    <div className="ml-auto flex items-center gap-1.5">
                        <button
                            data-testid="abuse-events-refresh"
                            onClick={onRefresh}
                            disabled={refreshing}
                            className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white border border-[#2A3655] hover:border-amber-500/60 px-2 py-1 rounded-sm"
                        >
                            {refreshing ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                                <RefreshCw className="w-3 h-3" />
                            )}
                            Refresh
                        </button>
                        {!isReviewer && events.length > 0 && (
                            <button
                                data-testid="abuse-events-clear"
                                onClick={onClearLog}
                                className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200 border border-red-500/30 hover:border-red-400 px-2 py-1 rounded-sm"
                            >
                                <Trash2 className="w-3 h-3" />
                                Clear log
                            </button>
                        )}
                    </div>
                </div>
                {events.length === 0 ? (
                    <p
                        data-testid="abuse-events-empty"
                        className="text-xs text-slate-400 italic"
                    >
                        No abuse attempts logged yet. When someone tries to bypass
                        your signup rules, they'll show up here.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#2A3655]">
                                    <th className="text-left py-2 pr-3">When</th>
                                    <th className="text-left py-2 pr-3">Email</th>
                                    <th className="text-left py-2 pr-3">Reason</th>
                                    <th className="text-left py-2 pr-3">Matched</th>
                                    <th className="text-left py-2 pr-3">IP</th>
                                    <th className="text-left py-2">Device</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((e) => (
                                    <tr
                                        key={e.event_id}
                                        data-testid={`abuse-row-${e.event_id}`}
                                        className="border-b border-[#2A3655]/40 text-slate-300"
                                    >
                                        <td className="py-1.5 pr-3 font-mono text-[10px] text-slate-500">
                                            {(e.ts || "").replace("T", " ").slice(0, 19)}
                                        </td>
                                        <td className="py-1.5 pr-3 text-slate-200">{e.email_preview}</td>
                                        <td className="py-1.5 pr-3">
                                            <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${
                                                e.reason === "duplicate_identity"
                                                    ? "border-red-500/30 text-red-300 bg-red-500/10"
                                                    : "border-amber-500/30 text-amber-300 bg-amber-500/10"
                                            }`}>
                                                {e.reason.replace(/_/g, " ")}
                                            </span>
                                        </td>
                                        <td className="py-1.5 pr-3 text-slate-400">{e.matched_field}</td>
                                        <td className="py-1.5 pr-3 font-mono text-slate-400">{e.ip || "—"}</td>
                                        <td className="py-1.5 font-mono text-[10px] text-slate-500 truncate max-w-[180px]">
                                            {e.device_id || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Toggles */}
            <section
                data-testid="security-toggles-card"
                className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-4 md:p-5"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-[#00A3FF]" />
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                        Limit subscribers to 1 account per…
                    </h3>
                </div>
                <p className="text-xs text-slate-400 mb-4">
                    When enabled, a second signup with the same value gets a generic
                    "An account with these details already exists" error and is logged
                    above.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TOGGLES.map((t) => {
                        const Icon = t.icon;
                        const checked = !!cfg[t.key];
                        const disabled = isReviewer || t.locked;
                        return (
                            <label
                                key={t.key}
                                data-testid={`security-toggle-${t.key}`}
                                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-sm border bg-[#0A1128]/60 cursor-pointer transition-colors ${
                                    checked
                                        ? "border-[#00A3FF]/60"
                                        : "border-[#2A3655] hover:border-[#00A3FF]/40"
                                } ${disabled ? "opacity-80 cursor-not-allowed" : ""}`}
                            >
                                <Icon className="w-4 h-4 text-[#00A3FF] mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-white">
                                            {t.label}
                                        </span>
                                        {t.locked && (
                                            <span className="text-[9px] uppercase tracking-wider text-slate-500 border border-slate-600 px-1 rounded-sm">
                                                always on
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        {t.hint}
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={(e) => updateField(t.key, e.target.checked)}
                                    className="mt-1 h-4 w-4 accent-[#00A3FF] cursor-pointer disabled:cursor-not-allowed"
                                    data-testid={`security-toggle-input-${t.key}`}
                                />
                            </label>
                        );
                    })}
                </div>
            </section>

            {/* Soft-delete window */}
            <section
                data-testid="soft-delete-card"
                className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-4 md:p-5"
            >
                <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-1">
                    Account deletion grace period
                </h3>
                <p className="text-xs text-slate-400 mb-4">
                    Deleted accounts are <strong>disabled</strong> for this many days
                    before being permanently purged. Users get a reactivation email
                    immediately on deletion and another one if they try to re-register.
                </p>
                <div className="flex items-center gap-3">
                    <input
                        type="range"
                        min={7}
                        max={90}
                        step={1}
                        value={cfg.soft_delete_days}
                        onChange={(e) =>
                            updateField("soft_delete_days", parseInt(e.target.value, 10))
                        }
                        disabled={isReviewer}
                        data-testid="soft-delete-days-slider"
                        className="flex-1 accent-[#00A3FF]"
                    />
                    <div
                        data-testid="soft-delete-days-value"
                        className="font-mono text-lg font-bold text-white w-14 text-right tabular-nums"
                    >
                        {cfg.soft_delete_days}d
                    </div>
                </div>
            </section>

            {/* Save */}
            {!isReviewer && (
                <div className="flex justify-end">
                    <Button
                        onClick={onSave}
                        disabled={saving}
                        data-testid="security-save-btn"
                        className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold h-10 px-5 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            "Save security settings"
                        )}
                    </Button>
                </div>
            )}
            {isReviewer && (
                <p
                    data-testid="security-reviewer-notice"
                    className="text-xs text-amber-300 italic text-right"
                >
                    Reviewer account — read-only.
                </p>
            )}
        </div>
    );
}
